import { useState } from 'react';
import { SquareMinus, SquarePlus } from 'lucide-react';
import type { ColumbusPlannerTermModel } from '@/api/generated/schemas';
import type { TermPlan, GroupCost, PaxCost } from '../../columbusTypes';
import { PlanEdit } from '../../pricing/components/PlanEdit';
import { PersonList } from '../../passengers/components/PersonList';
import { BuyTicketsDialog } from '../../passengers/components/BuyTicketsDialog';

interface Props {
  term: ColumbusPlannerTermModel;
  plan: TermPlan | undefined;
  effectivePlan: TermPlan;
  hasCustomPlan: boolean;
  groupCostNames: Pick<GroupCost, 'name'>[];
  paxCostNames: Pick<PaxCost, 'name'>[];
  maxTariffsCount: number;
  planKey: string;
  productId: number;
  destinationSeasonId: number;
  termsSerialId: number;

  onUpdateTariff: (
    planKey: string,
    index: number,
    field: 'pax' | 'retailPrice',
    value: number | null
  ) => void;
  onAddTariff: (planKey: string) => void;
  onRemoveTariff: (planKey: string, index: number) => void;
  onUpdateGroupCostName: (index: number, name: string) => void;
  onUpdateGroupCostPrice: (planKey: string, index: number, price: number | null) => void;
  onAddGroupCost: () => void;
  onRemoveGroupCost: (index: number) => void;
  onUpdatePaxCostName: (index: number, name: string) => void;
  onUpdatePaxCostPrice: (planKey: string, index: number, price: number | null) => void;
  onAddPaxCost: () => void;
  onRemovePaxCost: (index: number) => void;
  onUpdateTicketPrice: (
    planKey: string,
    field: 'depTicketPrice' | 'retTicketPrice',
    value: number | null
  ) => void;
}

function formatDateFull(date: Date | string): string {
  try {
    return (typeof date === 'string' ? new Date(date) : date).toLocaleDateString('cs-CZ', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return String(date);
  }
}

function formatDateShort(date: Date | string): string {
  try {
    return (typeof date === 'string' ? new Date(date) : date).toLocaleDateString('cs-CZ', {
      day: '2-digit',
      month: '2-digit',
    });
  } catch {
    return String(date);
  }
}

function addDays(dateStr: string, days: number): Date {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days);
  return d;
}

export function TermDetail({
  term,
  plan,
  effectivePlan,
  hasCustomPlan,
  groupCostNames,
  paxCostNames,
  maxTariffsCount,
  planKey,
  productId,
  destinationSeasonId,
  termsSerialId,
  onUpdateTariff,
  onAddTariff,
  onRemoveTariff,
  onUpdateGroupCostName,
  onUpdateGroupCostPrice,
  onAddGroupCost,
  onRemoveGroupCost,
  onUpdatePaxCostName,
  onUpdatePaxCostPrice,
  onAddPaxCost,
  onRemovePaxCost,
  onUpdateTicketPrice,
}: Props) {
  const [isPlanExpanded, setIsPlanExpanded] = useState(hasCustomPlan);
  const [buyTicketsOpen, setBuyTicketsOpen] = useState(false);

  const isMultiDay = term.nights != null && term.nights > 0;
  const title = isMultiDay
    ? `${formatDateShort(term.dateFrom)} – ${formatDateFull(addDays(term.dateFrom, term.nights!))} (${term.nights}n)`
    : formatDateFull(term.dateFrom);

  return (
    <div>
      {/* Title row with toggle */}
      <strong className="flex items-center gap-2 text-base">
        {title}
        <button
          type="button"
          data-testid={`term-plan-toggle-${term.id}`}
          className="text-phx-text-2 hover:bg-phx-surface-3 hover:text-phx-text rounded p-0.5 transition-colors"
          onClick={() => setIsPlanExpanded((e) => !e)}
        >
          {isPlanExpanded ? (
            <SquareMinus className="h-4 w-4" />
          ) : (
            <SquarePlus className="h-4 w-4" />
          )}
        </button>
      </strong>

      {/* Custom plan editor */}
      {isPlanExpanded && (
        <div className="mt-1.5">
          <PlanEdit
            planKey={planKey}
            plan={plan ?? effectivePlan}
            groupCostNames={groupCostNames}
            paxCostNames={paxCostNames}
            maxTariffsCount={maxTariffsCount}
            tickets={term.tickets}
            onUpdateTariff={(i, f, v) => onUpdateTariff(planKey, i, f, v)}
            onAddTariff={() => onAddTariff(planKey)}
            onRemoveTariff={(i) => onRemoveTariff(planKey, i)}
            onUpdateGroupCostName={onUpdateGroupCostName}
            onUpdateGroupCostPrice={(i, v) => onUpdateGroupCostPrice(planKey, i, v)}
            onAddGroupCost={onAddGroupCost}
            onRemoveGroupCost={onRemoveGroupCost}
            onUpdatePaxCostName={onUpdatePaxCostName}
            onUpdatePaxCostPrice={(i, v) => onUpdatePaxCostPrice(planKey, i, v)}
            onAddPaxCost={onAddPaxCost}
            onRemovePaxCost={onRemovePaxCost}
            onUpdateTicketPrice={(f, v) => onUpdateTicketPrice(planKey, f, v)}
            onBuyTickets={() => setBuyTicketsOpen(true)}
          />
        </div>
      )}

      {/* Person list */}
      <div className="mt-2">
        <PersonList term={term} plan={effectivePlan} />
      </div>

      {buyTicketsOpen && (
        <BuyTicketsDialog
          term={term}
          productId={productId}
          destinationSeasonId={destinationSeasonId}
          termsSerialId={termsSerialId}
          onClose={() => setBuyTicketsOpen(false)}
        />
      )}
    </div>
  );
}
